import { z } from 'zod';

import { CodeDataEditor, CodeLanguageSelect } from '@/components/Code/Code';
import TrashIcon from '@/icons/TrashIcon.svg?react';
import { tryParseJSON } from '@/utils/try-parse-json';

import { useLanguagePreference } from '../../hooks/useLanguagePreferenceContext/useLanguagePreference';
import { OpenAIToolCallsKwargsSchema } from '../Message.utils';
import CallIdInput from './CallIdInput';
import FunctionNameInput from './FunctionNameInput';

const CodeFunctionCallOaiToolEditor = ({
  value,
  onChange,
  readOnly,
}: {
  value: z.infer<typeof OpenAIToolCallsKwargsSchema>;
  onChange: (value: z.infer<typeof OpenAIToolCallsKwargsSchema>) => void;
  readOnly?: boolean;
}) => {
  const [userLang, setUserLang] = useLanguagePreference();

  return (
    <div className="flex flex-col divide-y divide-secondary">
      {value.tool_calls.map((toolCall, index) => {
        function handleToolCallChange(
          toolCall: (
            value: z.infer<
              typeof OpenAIToolCallsKwargsSchema
            >['tool_calls'][number]
          ) => z.infer<typeof OpenAIToolCallsKwargsSchema>['tool_calls'][number]
        ) {
          onChange({
            tool_calls: value.tool_calls.map((i, idx) => {
              if (idx === index) {
                return toolCall(i);
              }

              return i;
            }),
          } satisfies z.infer<typeof OpenAIToolCallsKwargsSchema>);
        }

        return (
          <div key={index} className="flex flex-col divide-y divide-secondary">
            <div className="grid grid-cols-[1fr,auto] items-center gap-2">
              <div className="flex flex-wrap items-center">
                <FunctionNameInput
                  value={toolCall.function.name}
                  onChange={(name) =>
                    handleToolCallChange((tool) => ({
                      ...tool,
                      function: { ...tool.function, name },
                    }))
                  }
                />

                <CallIdInput
                  value={toolCall.id}
                  onChange={(id) =>
                    handleToolCallChange((tool) => ({ ...tool, id }))
                  }
                />
              </div>

              <button
                type="button"
                className="mr-4 flex items-center justify-center rounded-md border border-secondary p-1 opacity-0 transition-all focus-within:opacity-100 group-hover:opacity-100"
                onClick={() => {
                  onChange({
                    tool_calls: value.tool_calls.filter(
                      (_, idx) => idx !== index
                    ),
                  } satisfies z.infer<typeof OpenAIToolCallsKwargsSchema>);
                }}
              >
                <TrashIcon />
              </button>
            </div>

            <CodeDataEditor
              readOnly={readOnly}
              language={userLang}
              value={tryParseJSON(toolCall.function.arguments, true)}
              onChange={(data) => {
                handleToolCallChange((tool) => ({
                  ...tool,
                  function: {
                    ...tool.function,
                    arguments: JSON.stringify(data),
                  },
                }));
              }}
            />
          </div>
        );
      })}

      <div className="flex items-center justify-start">
        <CodeLanguageSelect value={userLang} onChange={setUserLang} />
      </div>
    </div>
  );
};

export default CodeFunctionCallOaiToolEditor;
